import { Slugs } from 'common/urls/Slugs';
import React, { useCallback, useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import { useReviewsModalQuery } from 'src/gqlReactTypings.generated.d';
import { LoadingWithHeight } from 'src/shared/design-system/Loading';
import { Modal } from 'src/shared/design-system/Modal';
import { useHistory } from 'src/shared/hooks/useHistory';
import { Routes } from 'src/shared/Routes';
import { getSlug, getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { usePreviousPage } from 'src/util/history';
import styled from 'styled-components';
import { ScreenWidth, Spacing } from 'web-common/src/shared/styles';
import { ModalContent } from './ModalContent/ModalContent';

const LargeModal = styled(Modal)`
  border-radius: ${Spacing.HALF};
  width: 100%;
  max-width: 650px;
  height: 90vh;
  padding: ${Spacing.SINGLE} 0 0 0;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  row-gap: 0;

  @media (max-width: ${ScreenWidth.SMALL}) {
    border-radius: 0;
    height: 100%;
    width: 100%;
  }
`;

interface IProps {
  shefId: string;
  validDishes: string[];
}

export const ReviewsModal: React.FC<IProps> = ({ shefId, validDishes }) => {
  const { data, loading, fetchMore } = useReviewsModalQuery({
    variables: {
      shefId,
      limit: 10,
      offset: 0,
    },
  });

  const publicReviews = data?.publicReviews;

  const loadMore = useCallback(() => {
    fetchMore({ variables: { offset: publicReviews?.reviews.length } }).catch(console.error);
  }, [publicReviews?.reviews.length, fetchMore]);

  const showLoadMore = publicReviews?.total !== publicReviews?.reviews.length;

  const match = useRouteMatch();
  const querySlug = getSlug(match, Slugs.QUERY);
  const idSlug = getSlug(match, Slugs.ID);

  const slugs = useMemo(
    () => [
      { slug: Slugs.ID, value: idSlug },
      { slug: Slugs.QUERY, value: querySlug },
    ],
    [idSlug, querySlug]
  );

  const shefPath = getSluggedPath(Routes.CONSUMER_ORDER_ONE_TIME, slugs);
  const history = useHistory();
  const { matchesRoute, previousUrl } = usePreviousPage();

  const onClose = useCallback(() => {
    if (
      previousUrl &&
      (matchesRoute(Routes.CONSUMER_ORDER_ONE_TIME) || matchesRoute(Routes.CONSUMER_ORDER_EDIT_CART))
    ) {
      history.goBack();
    } else {
      history.push(shefPath);
    }
  }, [history, matchesRoute, previousUrl, shefPath]);

  const isOpen = useRouteMatch(Routes.CONSUMER_ORDER_REVIEWS);

  return (
    <LargeModal isShowing={Boolean(isOpen)} onClose={onClose} blockScrollOnMount={false} showCloseButton isCloseOnLeft>
      {!data || !publicReviews ? (
        <LoadingWithHeight />
      ) : (
        <ModalContent
          availableDishes={validDishes}
          isLoading={loading}
          loadMoreReviews={loadMore}
          numOrders={data.shef.totalOrders}
          numRatings={data.shef.rating?.count ?? 0}
          reviews={publicReviews.reviews}
          shefId={shefId}
          shefName={data.shef.publicName}
          showLoadMoreButton={showLoadMore}
          timeZone={data.shef.timeZone}
        />
      )}
    </LargeModal>
  );
};
